import { faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { AwesomeIcon, Body, Box, defaultTheme } from '@hover/blueprint';
import { useMachine } from '@xstate/react';
import React, { useCallback, useEffect } from 'react';
import { animated, useTransition } from 'react-spring';
import { useToaster } from 'shared/providers/Toaster';
import styled from 'styled-components';

import { Component as Share } from 'src/assets/icons/ui/share.svg';
import { animationConfig } from 'src/modules/designStudio/helpers/animationConfig';
import { ExtendedMacheteViewer } from 'src/modules/designStudio/helpers/machete';
import { createMachine } from 'src/modules/designStudio/machines/share';
import { Button } from 'src/shared/components';
import { SlideUpCard } from 'src/shared/components/SlideUpCard';
import { useEventListener, useTranslate } from 'src/shared/hooks';

import { FastLoader } from '../../FastLoader';
import { TOOLBAR_HEIGHT } from '../../Toolbar';
import { CardHeader } from '../CardHeader';

// Toolbar height + margin
const TOAST_OFFSET = TOOLBAR_HEIGHT + 16;

interface PropTypes {
  closeMenu: () => void;
  headerText: string;
  viewer: ExtendedMacheteViewer | null;
  jobId: number;
}

const Row = styled(Button)`
  border-radius: 0;
  border-bottom: 1px solid ${defaultTheme.colors.neutral200};
  ${({ disabled }) =>
    disabled
      ? `
    opacity: 0.3;
  `
      : ''}
  & div {
    justify-content: space-between;
  }
`;

const CancelButton = styled(Button)`
  border-radius: 0;
  background-color ${defaultTheme.colors.neutral100};
  color: ${defaultTheme.colors.neutral600};
  width: 100%;
`;

const Content = styled(Box)`
  & ${Row}:nth-child(1) {
    border-top: 1px solid ${defaultTheme.colors.neutral200};
  }
`;

const Icon = styled(AwesomeIcon).attrs({ color: 'neutral700' })`
  margin-left: auto;
`;

const ShareCard = ({ closeMenu, jobId, headerText, viewer }: PropTypes) => {
  const t = useTranslate('designStudio');
  const { addToast, removeToast } = useToaster();

  const [machine, send] = useMachine(
    createMachine({
      jobId,
      shareLink: undefined,
      shareToken: undefined,
      viewer,
      addToast,
      removeToast,
      closeMenu,
      translator: t,
      loading: false,
    }),
  );

  useEffect(() => {
    send('CREATE_SHARE');
  }, [send]);

  const handleEvent = useCallback(() => {
    // adds a toast notifying user share has been successful
    addToast({
      title: t('share.shareSuccessful'),
      subtitle: t('share.onePersonInvited'),
      icon: Share,
      onPress: removeToast,
      offset: -TOAST_OFFSET,
    });
    // close menu when receiving event from RN
    closeMenu();
  }, [closeMenu, t]);

  // Add a listener to handle callback from RN app
  useEventListener('share-finished', handleEvent);

  const disabled = !machine.context.shareLink;
  const { loading } = machine.context;

  const loadingTransition = useTransition(
    loading,
    null,
    animationConfig.fadeInAnimation,
  );

  return (
    <SlideUpCard disabled paddingBottom={0} paddingLeft={0} paddingRight={0}>
      <CardHeader
        headerText={headerText}
        marginBottom={0}
        padding={400}
        rightActionHandler={closeMenu}
        rightIcon={faTimes}
      />

      <Content
        alignItems="center"
        flex={1}
        flexDirection="column"
        justifyContent="space-between"
        width="100%"
      >
        <Box alignItems="center" flex={1} flexDirection="column">
          {loadingTransition.map(({ item: isLoading, props: styles, key }) =>
            isLoading ? (
              <animated.div key={key} style={styles}>
                <FastLoader />
              </animated.div>
            ) : (
              <animated.div
                key={key}
                style={{ position: 'absolute', width: '100%', ...styles }}
              >
                <Box flexDirection="column" width="100%">
                  <Row
                    aria-label="share-to"
                    disabled={disabled}
                    fill="minimal"
                    flex={1}
                    height="100%"
                    label="Share To"
                    onPress={() => send('SHARE_TO_SHARE_SHEET')}
                    width="100%"
                  >
                    <Body size={500}>{t('share.shareTo')}</Body>
                    <Icon icon={faChevronRight} />
                  </Row>
                  <Row
                    aria-label="copy-link"
                    disabled={disabled}
                    fill="minimal"
                    height="100%"
                    label="Copy Link"
                    onPress={() => send('COPY_LINK')}
                  >
                    <Body size={500}> {t('share.copyLink')}</Body>
                    <Icon icon={faChevronRight} />
                  </Row>
                </Box>
              </animated.div>
            ),
          )}
        </Box>
        <CancelButton
          aria-label="cancel-share"
          fill="minimal"
          height="80px"
          label="Cancel"
          onPress={closeMenu}
        >
          {t('share.cancel')}
        </CancelButton>
      </Content>
    </SlideUpCard>
  );
};

export { ShareCard };
